import {
  AvailabilityData,
  SitePRResponse,
  useSiteHealthEvents,
  useSitePR,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import {
  LoadingSpinner,
  SummaryBoxMetric,
  SummaryBoxMetrics,
} from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import React, { useContext } from "react";
import { UserContext } from "../../auth/UserContext";
import { isDemoModeEnabled } from "./demoMode";

const GoodStateIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <img
    src={Icons.circleGreenCheckmark}
    alt="Good"
    width={size}
    height={size}
    style={{ verticalAlign: "middle", marginRight: "8px" }}
  />
);

const formatAvailabilityPct = (pct: number | null): string => {
  if (pct === null) return "N/A";
  if (pct === 100) return "100%";
  return `${pct.toFixed(3)}%`;
};

const formatPrPct = (pct: number): string => `${pct.toFixed(1)}%`;

const iconForRyg = (ryg: string | null | undefined) =>
  ryg === "green" ? <GoodStateIcon /> : null;

const LoadingBox: React.FC<{ header: string }> = ({ header }) => (
  <SummaryBoxMetrics metrics={[]} header={header} grid={false}>
    <LoadingSpinner $border="solid #ffffff" $margin="20px auto" />
  </SummaryBoxMetrics>
);

// The hooks below hit admin-only endpoints (/site/<id>/health/pr and
// /health/events) and 401 for any non-InRange user. We must therefore not
// even mount them unless the gate passes — keeping them in the parent and
// short-circuiting only the JSX would still fire 401 storms (CUSTOMER-APP-P,
// ~12k events). Splitting into outer (gate) + inner (hooks) ensures the
// queries are never registered for non-InRange users.
const OperationalDemoMetrics: React.FC<{
  site: Site;
  orgDemo: boolean;
}> = ({ site, orgDemo }) => {
  const { user } = useContext(UserContext);
  if (!isDemoModeEnabled(orgDemo, user) || site.totalInstalledCapacity === 0) {
    return null;
  }
  return <OperationalDemoMetricsInner site={site} />;
};

const OperationalDemoMetricsInner: React.FC<{ site: Site }> = ({ site }) => {
  const { prQuery } = useSitePR(site.id);
  const { healthEvents } = useSiteHealthEvents(site.id);

  return (
    <>
      <HealthStatusBox
        isLoading={healthEvents.isLoading}
        openEventCount={healthEvents.data?.openEventCount}
      />
      <PerformanceRatioBox isLoading={prQuery.isLoading} prData={prQuery.data} />
      <PvSystemAvailabilityBox
        isLoading={healthEvents.isLoading}
        availability={healthEvents.data?.availability}
      />
    </>
  );
};

const PerformanceRatioBox: React.FC<{
  isLoading: boolean;
  prData: SitePRResponse | undefined;
}> = ({ isLoading, prData }) => {
  if (isLoading) return <LoadingBox header="Guaranteed PR (GPR)" />;
  const gpr = prData?.gpr;
  if (!gpr || (!gpr.lastMonth && !gpr.last12Months)) {
    return null;
  }

  const metrics: SummaryBoxMetric[] = [];
  if (gpr.lastMonth) {
    metrics.push({
      value: formatPrPct(gpr.lastMonth.pct),
      valueSubText: (
        <span>
          {iconForRyg(gpr.lastMonth.ryg)}
          {`Last month: ${gpr.lastMonth.periodLabel}`}
        </span>
      ),
    });
    if (gpr.lastMonth.prevMonthDelta != null) {
      const delta = gpr.lastMonth.prevMonthDelta;
      metrics.push({
        value:
          delta < 0
            ? `↓ ${Math.abs(delta).toFixed(1)} % pts`
            : delta > 0
              ? `↑ ${Math.abs(delta).toFixed(1)} % pts`
              : "No change",
        valueSubText: "Trend since prev month",
      });
    }
  }
  if (gpr.last12Months) {
    metrics.push({
      value: formatPrPct(gpr.last12Months.pct),
      valueSubText: (
        <span>
          {iconForRyg(gpr.last12Months.ryg)}
          {gpr.last12Months.periodLabel}
        </span>
      ),
    });
  }

  return (
    <SummaryBoxMetrics
      metrics={metrics}
      header={"Guaranteed PR (GPR)"}
      grid={false}
    />
  );
};

const PvSystemAvailabilityBox: React.FC<{
  isLoading: boolean;
  availability: AvailabilityData | undefined;
}> = ({ isLoading, availability }) => {
  if (isLoading) return <LoadingBox header="PV System Availability" />;
  if (!availability) return null;
  if (!availability.lastMonth && !availability.last12Months) return null;

  const metrics: SummaryBoxMetric[] = [];
  if (availability.lastMonth) {
    metrics.push({
      value: formatAvailabilityPct(availability.lastMonth.pct),
      valueSubText: (
        <span>
          {iconForRyg(availability.lastMonth.ryg)}
          {`Last month: ${availability.lastMonth.periodLabel}`}
        </span>
      ),
    });
  }
  if (availability.last12Months) {
    metrics.push({
      value: formatAvailabilityPct(availability.last12Months.pct),
      valueSubText: (
        <span>
          {iconForRyg(availability.last12Months.ryg)}
          {availability.last12Months.periodLabel}
        </span>
      ),
    });
  }

  return (
    <SummaryBoxMetrics
      metrics={metrics}
      header={"PV System Availability"}
      grid={false}
    />
  );
};

const HealthStatusBox: React.FC<{
  isLoading: boolean;
  openEventCount: Record<string, number> | undefined;
}> = ({ isLoading, openEventCount }) => {
  if (isLoading) return <LoadingBox header="Health status" />;
  if (!openEventCount) return null;

  const openEvents = Object.values(openEventCount).reduce((a, b) => a + b, 0);
  const isHealthy = openEvents === 0;

  const metrics: SummaryBoxMetric[] = [
    {
      value: isHealthy ? "Healthy" : `${openEvents} open`,
      valueSubText: (
        <span>
          {isHealthy && <GoodStateIcon />}
          {isHealthy ? "No open issues" : "Open health events"}
        </span>
      ),
    },
  ];

  return (
    <SummaryBoxMetrics
      metrics={metrics}
      header={"Health status"}
      grid={false}
    />
  );
};

export default OperationalDemoMetrics;
